import React, { useEffect, useState } from 'react';
import { GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, GeneralUserRole, REST_API, SystemAdminRole } from '@awp/constants';
import { Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { FormInput, FormList, FormTextArea } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import { selectPermissions } from '@awp/stores/dropdownsSlice';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import isEqual from 'lodash/isEqual';
import { xor } from 'lodash';
import { getCrud } from '@awp/shared/Crud';

const RoleEdit = ({ setMode, mode, roleId, data, setData, setLoading, loading }) => {
  const [roleData, setRoleData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);

  const permissionsDropdown = useSelector(selectPermissions);
  const permissions = permissionsDropdown.options;
  const [initialDto, setInitialDto] = useState({});
  const mapDTO = (x) => {
    return {
      name: x.Name ?? '',
      description: x.Description ?? '',
      permissions: x.PermissionIds ?? defaultPermissionIds,
    };
  };
  const defaultPermissionIds = data.find((x) => x.Name === GeneralUserRole)?.PermissionIds ?? [];

  const crud = getCrud('User Role', mode);
  const { readOnly, title, successMessage } = crud;

  useEffect(() => {
    if (mode === 'create') {
      setRoleData({ Name: '', Description: '', PermissionIds: defaultPermissionIds });
      setInitialDto(mapDTO(roleData));
      return;
    }

    setLoading(true);
    const url = REST_API.ROLES + roleId;

    GetJson(url)
      .then((response) => response.json())
      .then((role) => {
        setRoleData(role);
        setInitialDto(mapDTO(role));
        setLoading(false);
      });
  }, []);

  const updateRole = (setSubmitting, resetForm, setErrors, roleDto) => {
    const url = REST_API.ROLES + roleDto.Id;

    PutJson(url, roleDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const role = await response.json();
          setRoleData(role);
          updateData(role);
          setInitialDto(mapDTO(role));
          resetForm({
            values: mapDTO(role),
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Role: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createRole = (setSubmitting, resetForm, setErrors, roleDto) => {
    const url = REST_API.ROLES;

    PostJson(url, roleDto)
      .then(async (response) => {
        if (response.ok) {
          const role = await response.json();
          setInitialDto(mapDTO(role));
          setRoleData(role);
          updateData(role);
          resetForm({
            values: mapDTO(role),
          });
          toast.success(successMessage);
          setMode('view');
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Role: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const roleDto = {
      ...roleData,
      Name: values.name,
      Description: values.description,
      PermissionIds: values.permissions,
    };

    if (mode === 'edit') {
      updateRole(setSubmitting, resetForm, setErrors, roleDto);
    } else {
      createRole(setSubmitting, resetForm, setErrors, roleDto);
    }
  };

  const updateData = (role) => {
    if (mode === 'edit') {
      const index = data.findIndex((x) => x.Id === role.Id);
      if (index < 0) return;
      data[index] = role;
      setData([...data]);
    } else {
      setData([...data, role]);
    }
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={mapDTO(roleData)}
            validateOnMount={true}
            validationSchema={Yup.object({
              name: Yup.string()
                //.matches(/^[a-z0-9 ]+$/i, 'Special characters are not allowed')
                .max(50, 'Maximum 50 characters are allowed')
                .required('Role name is a required field'),
              description: Yup.string()
                //.matches(/^[a-z0-9 ,().]+$/i, 'Special characters are not allowed')
                .max(2000, 'Maximum 2000 characters are allowed')
                .required('Role description is a required field'),
              permissions: Yup.array().min(1, 'At least one permission must be selected'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => {
              const { permissions: propsPermissions, ...propsValues } = props.values;
              const { permissions: initialDtoPermissions, ...myInitialDto } = initialDto;
              const myIsPristine =
                isEqual(propsValues, myInitialDto) && xor(propsPermissions, initialDtoPermissions)?.length === 0;

              return (
                <Form>
                  <span className="awp-text-btn" onClick={() => setMode('list')}>
                    List User Roles
                  </span>
                  <span className="mx-3">&gt;</span>
                  <span>{title}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput
                            label="Role Name"
                            instruction="(50 Characters max)"
                            readOnly={readOnly || roleData.IsProtected === 'Y'}
                            name="name"
                            id="name"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea
                            readOnly={readOnly}
                            label="Role Description"
                            instruction="(2,000 Characters max)"
                            id="description"
                            name="description"
                            rows={3}
                          />
                        </Col>
                      </Row>
                      <hr className="solid" style={{ marginBottom: '2rem' }}></hr>
                      <Row>
                        <FormList
                          className="mt-3"
                          id="permissions"
                          name="permissions"
                          permissions={permissions}
                          label="Permissions"
                          colLabel="Description of User Permission"
                          readOnly={readOnly}
                        />
                      </Row>
                    </Card.Body>
                    <Card.Footer>
                      {!readOnly ? (
                        <AwpCloseCancelButtons
                          props={props}
                          isPristine={myIsPristine || props.isSubmitting}
                          hasAccess={true}
                          onConfirmedCancel={() => {
                            props.resetForm();
                            setMode('list');
                          }}
                        />
                      ) : (
                        <>
                          {' '}
                          <div className="mr-3 mr-mini-0">
                            <Button className="mr-2" variant="outline-primary" onClick={() => setMode(FORM_MODE.LIST)}>
                              Close
                            </Button>
                            {roleData.Name !== SystemAdminRole && (
                              <Button
                                className="mr-2 mr-mini-0"
                                variant="outline-primary"
                                onClick={() => setMode('edit')}
                              >
                                Edit Role
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </Card.Footer>
                  </Card>
                </Form>
              );
            }}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default RoleEdit;
